import { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAiHealthScoreStyles } from '../../utils/aiUtils';
import { createCustomerToken, fetchCustomerMe, fetchPublicOrder, removePublicOrderItem, setCustomerApproval, fetchPublicCompanyConfig } from '../../api/orders';
import { fetchActiveBanners, Banner } from '../../api/banners';
import type { OrderResponse } from '../../api/orders';


const COOKIE_NAME_PREFIX = 'cust_approval_token_';

const getCookie = (name: string): string | null => {
  const parts = document.cookie.split(';').map((p) => p.trim());
  for (const p of parts) {
    if (p.startsWith(name + '=')) return decodeURIComponent(p.slice(name.length + 1));
  }
  return null;
};

const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires = ${expires}; path =/; SameSite=Lax`;
};

const CustomerOrderApproval: FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const cookieName = useMemo(() => `${COOKIE_NAME_PREFIX}${orderId ?? ''}`, [orderId]);

  const [token, setToken] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [me, setMe] = useState<{ role: 'CUSTOMER'; email: string; orderId: string } | null>(null);
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [approveChecked, setApproveChecked] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState<string>('');
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeItemId, setRemoveItemId] = useState<string | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [removeReminderDays, setRemoveReminderDays] = useState('');
  const [removing, setRemoving] = useState(false);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const printInvoice = () => {
    if (!order) return;
    const symbol = currencySymbol || '';
    const fmt = (v: unknown) => {
      const n = typeof v === 'number' ? v : Number(v ?? 0);
      if (Number.isNaN(n)) return symbol ? `${symbol} 0.00` : '0.00';
      const formatted = n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return symbol ? `${symbol} ${formatted}` : formatted;
    };
    const locationName = (order as any)?.location?.name || order.locationCode || '';
    const customerName = order.customer ? `${order.customer.firstName ?? ''} ${order.customer.lastName ?? ''}`.trim() || (order.customer.email || '') : '';
    const customerContact = order.customer?.mobileNumber || order.customer?.email || '';
    const vehicleLine = order.customerVehicle ? [order.customerVehicle.vehicleBrand?.name, order.customerVehicle.vehicleModel?.name].filter(Boolean).join(' · ') : '';
    const created = order.createdAt ? new Date(order.createdAt).toLocaleString() : '';

    const rows = (order.items || []).map((it) => `
      <tr>
        <td>${(it.description || it.productVariant?.name || 'Item')}</td>
        <td class="num">${it.quantity}</td>
        <td class="num">${fmt(it.unitPrice)}</td>
        <td class="num">${fmt(it.discountTotal)}</td>
        <td class="num">${fmt(it.taxTotal)}</td>
        <td class="num">${fmt(it.lineTotal)}</td>
      </tr>
    `).join('');

    const html = `<!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Invoice ${order.orderNumber}</title>
        <style>
          body{font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif; color:#0f172a;}
          .wrap{max-width:900px;margin:24px auto;padding:24px;border:1px solid #e2e8f0;border-radius:12px}
          h1{margin:0 0 4px 0;font-size:20px}
          .muted{color:#64748b;font-size:12px}
          table{width:100%;border-collapse:collapse;margin-top:16px;font-size:12px}
          th,td{padding:8px;border-bottom:1px solid #e2e8f0}
          th{background:#f8fafc;text-align:left}
          td.num, th.num{text-align:right}
          .totals{max-width:320px;margin-left:auto;margin-top:12px;font-size:13px}
          .totals div{display:flex;justify-content:space-between;margin:4px 0}
          .grand{font-weight:700;font-size:14px}
          @media print {.wrap{border:none}}
        </style>
      </head>
      <body>
        <div class="wrap">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px">
            <div>
              <h1>Invoice</h1>
              <div class="muted">Order #: ${order.orderNumber}</div>
              <div class="muted">Created: ${created}</div>
              <div class="muted">Location: ${locationName}</div>
            </div>
            <div style="text-align:right">
              <div><strong>Customer</strong></div>
              <div class="muted">${customerName || '—'}</div>
              <div class="muted">${customerContact || ''}</div>
              <div class="muted">${vehicleLine || ''}</div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th class="num">Qty</th>
                <th class="num">Unit</th>
                <th class="num">Discount</th>
                <th class="num">Tax</th>
                <th class="num">Total</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="totals">
            <div><span>Subtotal</span><span>${fmt(order.subtotalAmount)}</span></div>
            <div><span>Discount</span><span>${fmt(order.discountTotal)}</span></div>
            <div><span>Tax</span><span>${fmt(order.taxTotal)}</span></div>
            <div class="grand"><span>Grand total</span><span>${fmt(order.grandTotal)}</span></div>
          </div>
        </div>
        <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 200); };</script>
      </body>
    </html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  useEffect(() => {
    const existing = orderId ? getCookie(cookieName) : null;
    if (!existing || !orderId) {
      setChecking(false);
      return;
    }
    // Optionally verify token
    fetchCustomerMe(existing)
      .then((res) => {
        if (res.ok && res.user && res.user.orderId === orderId) {
          setToken(existing);
          setMe(res.user);
        } else {
          setToken(null);
        }
      })
      .catch(() => setToken(null))
      .finally(() => setChecking(false));
  }, [cookieName, orderId]);

  const handleSubmit = async () => {
    if (!orderId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await createCustomerToken({ email, orderId, otp });
      if (res.ok && res.token) {
        setCookie(cookieName, res.token, 1); // 1 day
        setToken(res.token);
        // Optionally fetch me for display
        const meRes = await fetchCustomerMe(res.token);
        if (meRes.ok && meRes.user) setMe(meRes.user);
      } else {
        setError('Invalid OTP or email.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create token');
    } finally {
      setSubmitting(false);
    }
  };

  // Load public company config for currency symbol
  useEffect(() => {
    fetchPublicCompanyConfig()
      .then((res) => {
        if (res.ok && res.data?.currencySymbol) setCurrencySymbol(res.data.currencySymbol);
      })
      .catch(() => { });
  }, []);

  // Load active banners for customer view
  useEffect(() => {
    fetchActiveBanners()
      .then((data) => setBanners(data))
      .catch(() => setBanners([]));
  }, []);

  // Auto-advance banner carousel
  useEffect(() => {
    if (banners.length <= 1) return;
    const id = window.setInterval(() => {
      setActiveBannerIndex((prev) => (prev + 1) % banners.length);
    }, 7000);
    return () => window.clearInterval(id);
  }, [banners.length]);

  if (checking) {
    return (
      <div className="min-h-[calc(100vh-220px)] bg-sky-50/60 py-8">
        <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-6 md:p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)] text-sm text-slate-600">
          Checking access…
        </div>
      </div>
    );
  }

  // If token exists, show placeholder order-approval UI (empty for now)
  if (token) {
    // Load order
    if (!order && !loadingOrder && orderId) {
      setLoadingOrder(true);
      fetchPublicOrder(orderId)
        .then((o) => { setOrder(o); if (!notes) { setNotes(o.customerNotes ?? ''); } })
        .catch(() => setOrder(null))
        .finally(() => setLoadingOrder(false));
    }

    const handleRemove = (itemId: string) => {
      setRemoveItemId(itemId);
      setRemoveReason('');
      setRemoveReminderDays('');
      setRemoveOpen(true);
    };

    const confirmRemove = async () => {
      if (!orderId || !token || !removeItemId) return;
      setRemoving(true);
      try {
        const payload: { itemId: string; token: string; reasons?: string | null; reminderAfterDays?: number | null } = {
          itemId: removeItemId,
          token,
        };
        if (removeReason.trim()) payload.reasons = removeReason.trim();
        if (removeReminderDays.trim()) payload.reminderAfterDays = Number(removeReminderDays);
        const updated = await removePublicOrderItem(orderId, payload);
        setOrder(updated);
        setRemoveOpen(false);
        setRemoveItemId(null);
      } catch {
        // keep modal open for retry
      } finally {
        setRemoving(false);
      }
    };

    const handleApprove = async () => {
      if (!orderId || !token) return;
      setSaving(true);
      try {
        const updated = await setCustomerApproval(orderId, { approve: true, customerNotes: notes || null, token });
        setOrder(updated);
        setApproveChecked(true);
      } catch { }
      setSaving(false);
    };

    const approved = !!order?.customerApproved;
    const approvedAt = order?.customerApprovedAt ? new Date(order.customerApprovedAt).toLocaleString() : null;
    return (
      <div className="min-h-[calc(100vh-220px)] bg-sky-50/60 p-8">
        <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-6 md:p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
          <div className="mx-auto w-full max-w-5xl space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div>
                <h1 className="text-lg font-semibold text-slate-800">{order?.orderNumber || 'Order'}</h1>
                <p className="text-base font-semibold text-slate-800">{((order as any)?.location?.name as string) || order?.locationCode || '—'}</p>
                <p className="text-[11px] text-slate-400">Created: {order?.createdAt ? new Date(order.createdAt).toLocaleString() : '—'}</p>
              </div>
              <div className="flex items-center gap-2">
                {order ? (
                  <button
                    type="button"
                    onClick={printInvoice}
                    className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    Print invoice (PDF)
                  </button>
                ) : null}
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                  onClick={() => { setCookie(cookieName, '', -1); setToken(null); setMe(null); }}
                >
                  Logout
                </button>
              </div>
            </div>

            {/* Customer-facing banner carousel */}
            {banners.length > 0 ? (
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                <div className="relative h-40 md:h-56">
                  {banners.map((banner, index) => {
                    const isActive = index === activeBannerIndex;
                    const image = (
                      <img
                        src={banner.imageUrl}
                        alt="Banner"
                        className="h-full w-full object-cover"
                      />
                    );
                    const inner = banner.actionUrl ? (
                      <a
                        href={banner.actionUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="block h-full w-full"
                      >
                        {image}
                      </a>
                    ) : (
                      image
                    );

                    return (
                      <div
                        key={banner.id}
                        className={`absolute inset-0 transition-opacity duration-700 ${isActive ? 'opacity-100' : 'opacity-0'
                          }`}
                      >
                        {inner}
                      </div>
                    );
                  })}
                </div>
                {banners.length > 1 ? (
                  <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
                    {banners.map((banner, index) => (
                      <button
                        key={banner.id}
                        type="button"
                        className={`h-2.5 w-2.5 rounded-full border border-white/80 transition ${index === activeBannerIndex
                          ? 'bg-white'
                          : 'bg-slate-500/40'
                          }`}
                        onClick={() => setActiveBannerIndex(index)}
                        aria-label={`Go to banner ${index + 1}`}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/50 p-4 text-center text-slate-500">
                Banner placeholder (company promo / brand image)
              </div>
            )}

            {approved ? (
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                <div className="flex items-center justify-between">
                  <span>Approved by customer{approvedAt ? ` on ${approvedAt}` : ''}.</span>
                </div>
              </div>
            ) : null}

            {/* AI summary snippet (if available) */}
            {order && (order as any).aiAnalysis ? (() => {
              const analysis = (order as any).aiAnalysis;
              const scores = (analysis as any).scores ?? {};
              const overall = scores.overall ?? {};
              const styles = getAiHealthScoreStyles(overall.score);
              return (
                <div className={`flex flex-col gap-2 rounded-2xl border p-4 text-sm md:flex-row md:items-center md:justify-between ${styles.container}`}>
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide ${styles.label}`}>
                      AI vehicle health summary
                    </p>
                    <p className={`mt-1 text-sm ${styles.text}`}>
                      <span className="font-semibold">
                        {overall.score ?? '—'}
                      </span>{' '}
                      <span className={`text-xs uppercase tracking-wide ${styles.label}`}>
                        {overall.label ?? ''}
                      </span>
                      {overall.summary ? (
                        <span className={`ml-1 ${styles.label}`}>
                          — {overall.summary}
                        </span>
                      ) : null}
                    </p>
                  </div>
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg border border-indigo-500 px-3 py-1.5 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-100"
                      onClick={() => setAiModalOpen(true)}
                    >
                      View AI analysis
                    </button>
                  </div>
                </div>
              );
            })() : null}
            {/* Totals summary moved below cart */}

            {/* Customer + Vehicle details */}
            {order ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-slate-700">Customer</h3>
                  <div className="mt-2 text-sm text-slate-600">
                    <p>{order.customer ? `${order.customer.firstName ?? ''} ${order.customer.lastName ?? ''}`.trim() || (order.customer.email || 'Customer') : '—'}</p>
                    <p className="text-xs text-slate-500">{order.customer?.mobileNumber || order.customer?.email || '—'}</p>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-slate-700">Vehicle</h3>
                  <div className="mt-2 text-sm text-slate-600">
                    <p>
                      {order.customerVehicle
                        ? [
                          order.customerVehicle.vehicleBrand?.name,
                          order.customerVehicle.vehicleModel?.name,
                        ].filter(Boolean).join(' · ') || '—'
                        : '—'}
                    </p>
                    <p className="text-xs text-slate-500">Reg: {order.customerVehicle?.vehicleRegistrationNumber || '—'}</p>
                    {order.customerVehicle?.vehicleChassisNumber && (
                      <p className="text-xs text-slate-500">Chassis: {order.customerVehicle.vehicleChassisNumber}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-700">Your Cart</h2>
                {order ? (
                  <p className="text-xs text-slate-500">
                    {(() => {
                      const items = (order.items || []).length;
                      const units = (order.items || []).reduce((acc, it) => acc + Number(it.quantity || 0), 0);
                      return `${items} item${items !== 1 ? 's' : ''} • ${units} unit${units !== 1 ? 's' : ''}`;
                    })()}
                  </p>
                ) : null}
              </div>
              {loadingOrder ? (
                <p className="mt-3 text-sm text-slate-500">Loading…</p>
              ) : !order ? (
                <p className="mt-3 text-sm text-slate-500">No order details.</p>
              ) : (
                <div className="mt-3 overflow-auto">
                  <table className="min-w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Item</th>
                        <th className="px-4 py-3 font-semibold">Qty</th>
                        <th className="px-4 py-3 font-semibold">Unit price</th>
                        <th className="px-4 py-3 font-semibold">Discount</th>
                        <th className="px-4 py-3 font-semibold">Tax</th>
                        <th className="px-4 py-3 font-semibold">Total</th>
                        <th className="px-4 py-3 font-semibold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(order.items || []).map((it) => {
                        const currency = currencySymbol || '';
                        const fmt = (v: unknown) => {
                          const n = typeof v === 'number' ? v : Number(v ?? 0);
                          if (Number.isNaN(n)) return currency ? `${currency} 0.00` : '0.00';
                          const formatted = n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          return currency ? `${currency} ${formatted}` : formatted;
                        };
                        return (
                          <tr key={it.id} className="even:bg-slate-50/70">
                            <td className="px-4 py-3 text-slate-700">{it.description || it.productVariant?.name || 'Item'}</td>
                            <td className="px-4 py-3">{it.quantity}</td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-700">{fmt(it.unitPrice)}</td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-700">{fmt(it.discountTotal)}</td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-700">{fmt(it.taxTotal)}</td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-700">{fmt(it.lineTotal)}</td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-lg border border-danger/30 px-3 py-1.5 text-xs font-semibold text-danger transition hover:bg-danger/10"
                                onClick={() => handleRemove(it.id)}
                                disabled={approved}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Amount summary below cart */}
            {order ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="ml-auto w-full max-w-sm">
                  {(() => {
                    const currency = currencySymbol || '';
                    const fmt = (v: unknown) => {
                      const n = typeof v === 'number' ? v : Number(v ?? 0);
                      if (Number.isNaN(n)) return currency ? `${currency} 0.00` : '0.00';
                      const formatted = n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      return currency ? `${currency} ${formatted}` : formatted;
                    };
                    return (
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">Subtotal</span>
                          <span className="font-semibold text-slate-800">{fmt(order.subtotalAmount)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">Discount</span>
                          <span className="font-semibold text-slate-800">{fmt(order.discountTotal)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">Tax</span>
                          <span className="font-semibold text-slate-800">{fmt(order.taxTotal)}</span>
                        </div>
                        <div className="mt-2 h-px bg-slate-200" />
                        <div className="flex items-center justify-between">
                          <span className="text-slate-700">Grand total</span>
                          <span className="text-base font-semibold text-slate-900">{fmt(order.grandTotal)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : null}



            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-base font-semibold text-slate-700">Approval</h3>
              {approved && (
                <p className="mt-1 text-xs text-slate-500">Approved {approvedAt ? approvedAt : ''}</p>
              )}
              <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" className="h-4 w-4" checked={approveChecked || approved} onChange={(e) => setApproveChecked(e.target.checked)} disabled={approved} />
                I confirm and approve this estimate
              </label>
              <textarea
                className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                rows={3}
                placeholder="Add any notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={approved}
              />
              <div className="mt-3 text-right">
                <button
                  type="button"
                  disabled={!approveChecked || saving || approved}
                  onClick={handleApprove}
                  className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {saving ? 'Submitting…' : 'Approve'}
                </button>
              </div>
            </div>
            {/* Remove modal */}
            {removeOpen ? (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
                <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl">
                  <h3 className="text-base font-semibold text-slate-800">Remove item</h3>
                  <p className="mt-1 text-xs text-slate-500">Optional: capture a reason and a reminder in days.</p>
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-600">Reason</label>
                      <textarea
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                        rows={3}
                        value={removeReason}
                        onChange={(e) => setRemoveReason(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600">Reminder after (days)</label>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={removeReminderDays}
                        onChange={(e) => { const v = e.target.value; if (/^\d*$/.test(v)) setRemoveReminderDays(v); }}
                        className="mt-1 w-40 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:border-primary hover:text-primary disabled:opacity-60"
                      onClick={() => { if (!removing) { setRemoveOpen(false); setRemoveItemId(null); } }}
                      disabled={removing}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rounded-lg bg-danger px-3 py-1.5 text-sm font-semibold text-white hover:bg-danger/90 disabled:opacity-60"
                      onClick={confirmRemove}
                      disabled={removing}
                    >
                      {removing ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {/* AI analysis modal */}
            {aiModalOpen && order && (order as any).aiAnalysis ? (() => {
              const analysis = (order as any).aiAnalysis;
              const scores = (analysis as any).scores ?? {};
              const overall = scores.overall ?? {};
              const sections = scores.sections ?? {};
              const recs = (analysis as any).recommendations ?? {};
              const now = (recs.recommendedNow?.products ?? []) as Array<{
                id: string;
                name: string;
                reason: string;
              }>;
              const future = (recs.recommendedFuture?.products ?? []) as Array<{
                id: string;
                name: string;
                reason: string;
              }>;
              const sectionKeys: Array<keyof typeof sections> = [
                'tyres',
                'engine',
                'brakes',
                'suspension',
                'electrical',
                'body',
              ];
              return (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 px-4">
                  <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl">
                    <div className="mb-4 flex items-center justify-between">
                      <h2 className="text-lg font-semibold text-slate-900">
                        AI recommendations
                      </h2>
                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100"
                        onClick={() => setAiModalOpen(false)}
                      >
                        ✕
                      </button>
                    </div>
                    <div className="max-h-[70vh] overflow-y-auto pr-1 text-sm text-slate-700">
                      <div className="space-y-4">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <h3 className="text-sm font-semibold text-slate-900">
                            Overall heal
                          </h3>
                          {(() => {
                            const styles = getAiHealthScoreStyles(overall.score);
                            return (
                              <>
                                <p className={`mt-1 text-sm ${styles.text}`}>
                                  <span className="font-semibold">
                                    {overall.score ?? '—'}
                                  </span>{' '}
                                  <span className={`uppercase text-xs tracking-wide ${styles.label}`}>
                                    {overall.label ?? ''}
                                  </span>
                                </p>
                                {overall.summary ? (
                                  <p className={`mt-2 text-sm ${styles.text}`}>
                                    {overall.summary}
                                  </p>
                                ) : null}
                              </>
                            );
                          })()}
                        </div>

                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">
                            Section scores
                          </h3>
                          <div className="mt-2 grid gap-3 md:grid-cols-2">
                            {sectionKeys.map((key) => {
                              const keyName = String(key);
                              const sec = (sections as any)[keyName];
                              if (!sec) return null;
                              const secStyles = getAiHealthScoreStyles(sec.score);
                              return (
                                <div
                                  key={keyName}
                                  className={`rounded-lg border p-3 ${secStyles.container}`}
                                >
                                  <div className="flex items-center justify-between text-sm">
                                    <span className={`font-semibold ${secStyles.text}`}>
                                      {keyName.replace(/^\w/, (c) => c.toUpperCase())}
                                    </span>
                                    <span className={`text-xs font-medium ${secStyles.label}`}>
                                      {sec.score ?? '—'} • {sec.label ?? ''}
                                    </span>
                                  </div>
                                  {sec.summary ? (
                                    <p className={`mt-1 text-xs ${secStyles.text}`}>
                                      {sec.summary}
                                    </p>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">
                            Recommended now
                          </h3>
                          {now.length === 0 ? (
                            <p className="mt-1 text-sm text-slate-600">
                              No immediate actions.
                            </p>
                          ) : (
                            <ul className="mt-2 space-y-2 text-sm text-slate-700">
                              {now.map((item) => (
                                <li
                                  key={item.id}
                                  className="rounded-md bg-emerald-50 p-2"
                                >
                                  <div className="font-semibold text-emerald-900">
                                    {item.name}
                                  </div>
                                  <div className="text-xs text-emerald-800">
                                    {item.reason}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">
                            Recommended in future
                          </h3>
                          {future.length === 0 ? (
                            <p className="mt-1 text-sm text-slate-600">
                              No future recommendations.
                            </p>
                          ) : (
                            <ul className="mt-2 space-y-2 text-sm text-slate-700">
                              {future.map((item) => (
                                <li
                                  key={item.id}
                                  className="rounded-md bg-slate-50 p-2"
                                >
                                  <div className="font-semibold text-slate-900">
                                    {item.name}
                                  </div>
                                  <div className="text-xs text-slate-800">
                                    {item.reason}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })() : null}
          </div>
        </div>
      </div>
    );
  }

  // Otherwise show email + OTP form
  return (
    <div className="min-h-[calc(100vh-220px)] bg-sky-50/60 py-8">
      <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-6 md:p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
        <div className="mx-auto w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-slate-800">Verify Email</h1>
          <p className="mt-1 text-sm text-slate-500">Enter your email and OTP to proceed.</p>

          {error ? (
            <div className="mt-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{error}</div>
          ) : null}

          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-600">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600">OTP</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="6-digit code"
              />
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!email || !otp || submitting}
              className="mt-1 inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Signing in…' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>


  );
};

export default CustomerOrderApproval;
